// ─── SPA entry module — Phase 11 of the app.js split ────────────────────────
//
// This module is the `<script type="module" src="…">` target for index.html.
// It imports from every other split module (Phases 1-10 + 10.5) and wires up
// every DOM event listener, fetch, interval, and Supabase auth handler that
// previously lived at top-level scope in `web/static/app.js`.
//
// `app.js` is deleted in this phase — every back-compat copy, window-global
// bridge to internal modules, and intermediate shim disappears. The only
// `window.*` writes that remain are the four cross-page contracts identified
// in docs/design/app-js-split-plan-260522.md §8.2:
//
//   • window.__adatoolUserState   — consumed by e2e specs (test/e2e/test-token-model*.vitest.mjs)
//   • window.__adatoolTest        — consumed by e2e specs (test/e2e/e2e-test.mjs)
//   • window.__adaEnsureBootstrap — consumed by inline scripts on other SPA pages
//
// A handful of internal back-compat window bridges
// (window._adaRender / window._adaSmComputeUploadDiff /
// window._adaUserContextForUpload / window._adaAudienceLevelForUpload /
// window._adaRunTokenPreflight / window._adaUpdateQuotaBadge /
// window._adaMaxFileSizeMb / window._adaGetAuthProfile) remain set here as a
// transitional step: the spa/* modules still call them at call-time (so seam
// tests can pin the bridge surface). A follow-up Phase 12 will migrate those
// callsites to direct imports + retire the bridges, per CLAUDE.md
// "Update callsites; don't preserve legacy shims."
//
// No exports — this module is the entry point. Nothing imports from it.
// ─────────────────────────────────────────────────────────────────────────────
import { API, authFetch, sleep, esc } from './globals-boot.mjs';
import { jobState, ACTIVE_STATUSES, readEnvelope, } from './job-state.js';
import { REMEDIATE_EXTS, filterByExtension, expandZips, extractFolder, } from './extract.mjs';
import { uploadToastBegin, uploadToastDismiss, } from './upload-toast.mjs';
import { showSectionBanner, clearUploadMessages, updateMessagesSummary, refreshSystemBanner, updateSystemBanner, showRateLimitNotification as _showRateLimitNotification, } from './banners.mjs';
import { initAudienceLevel, initUserContextAltText, initEmbedFontsWithSubstitution, smComputeUploadDiff, showStructuralModToggleForUser, userContextAltTextForUpload, audienceLevelForUpload, embedFontsWithSubstitutionForUpload, } from './toggles.js';
import { startPolling, pollLoop as _pollLoop, } from './polling.mjs';
import { render, renderDonateSection, downloadChangeReport, } from './renderers.js';
import { renderSupportedFormatBubblesInto } from '../shared/supported-format-bubbles.js';
import { checkSupabaseAuth, registerOnAuthenticated, signInWithGoogle, signInWithGithub, signInWithMicrosoft, logout, setupDevBtn, linkInstitution, runTokenPreflight, getAuthProfile, getUserClass, getUserGroups, getUserInstitutionName, getUserTokenBalance, computeDrawOrderFromClient, canCoverWithDrawOrderFromClient, setUserStateForTest, } from './auth-and-billing.js';
import { uploadFiles } from './upload.mjs';
import { initDriveLink } from './drive-link.js';
import { handleSseFrame } from './sse-handler.js';
import { requireConsentIfNeeded } from './consent-dialog.js';
import { fileExtFromFilename, unitLabelForExt } from './init-helpers.js';
// ─── Logger handle ──────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- WHY: LHS can be `false` (window check), so ?? would leave false in the union; || is correct here
const log = (typeof window !== 'undefined' && window.adatool?.log) || {
    info: () => { }, warn: () => { }, error: () => { }, debug: () => { },
};
// ─── Internal back-compat bridges (transitional — to retire in Phase 12) ────
// The spa/* modules read these at call-time. They are SET here so the import
// graph stays one-way (init.ts → leaf modules) without forcing every leaf to
// be rewritten to import its sibling directly in this PR. Seam tests at
// web/static/spa/test/*.test.mjs pin the bridge surface; Phase 12 will
// migrate the callsites and retire the bridges + the seam-test stubs.
window._adaRender = () => render();
window._adaSmComputeUploadDiff = () => smComputeUploadDiff();
window._adaUserContextForUpload = () => userContextAltTextForUpload();
window._adaAudienceLevelForUpload = () => audienceLevelForUpload();
window._adaEmbedFontsWithSubstitutionForUpload = () => embedFontsWithSubstitutionForUpload();
window._adaRunTokenPreflight = async (valid) => runTokenPreflight(valid);
window._adaUpdateQuotaBadge = (_filesUploaded) => {
    // No-op: credit-based badges are re-synced from the server every 60 s
    // by the auth-and-billing module's _loadPricingLimits + periodic refresh.
    // (Mirrors app.js's no-op _updateQuotaBadge.)
};
window._adaGetAuthProfile = () => getAuthProfile();
// ─── Cross-page contracts (DO NOT retire — consumed by sibling pages) ──────
// Per docs/design/app-js-split-plan-260522.md §8.2.
window.__adatoolUserState = {
    getClass: () => getUserClass(),
    getBalance: () => ({ ...getUserTokenBalance() }),
    getInstitutionName: () => getUserInstitutionName(),
    getGroups: () => getUserGroups().slice(),
    // Test-side hook: lets specs prime the v2.1 group state without
    // round-tripping /api/account. Forwards to AuthClient.setForTest via
    // auth-and-billing's exposed singleton helper.
    setForTest: (cls, balance, groups) => {
        // Casts unfold the global d.ts's structural shape (Record<string,unknown>)
        // into auth-and-billing's typed shape. The e2e specs pass loose JS
        // objects so the runtime is OK; we just satisfy the typechecker.
        setUserStateForTest(cls, balance, groups);
    },
    computeDrawOrder: () => computeDrawOrderFromClient(),
    canCoverWithDrawOrder: (n) => canCoverWithDrawOrderFromClient(n),
};
// WHY: namespace all app-injected globals under window.adatool.
// 2026-05-25 migration — precursor to frontend-monkeypatch-to-DI Phase 3.
window.adatool = window.adatool || {};
// Self-serve purchase was removed with the Stripe strike (invoice-everywhere Beta;
// beta-billing-purdue-unlimited-no-dollars-260821 Phase 6). The commercialization
// flag is pinned OFF — its remaining reader (view-usage.ts) renders the no-purchase
// branch. The flag itself is removed with the backend in Phase 7.
window.adatool.commercialization_enabled = false;
// SPA-bootstrap (B-7 / Phase E billing-quota migration). Fire-and-forget —
// silently establishes the ada_spa_bootstrap cookie that anonymous
// /api/upload requires. Authenticated users don't need this cookie —
// _require_spa_mediation_or_auth short-circuits when an identity is present.
//
// Race: if the user clicks upload between page-load and the bootstrap
// response, the upload could 401. Mitigated by __adaEnsureBootstrap below,
// which the upload handler can await before retry. Exposed on window so the
// inline scripts in other SPA pages can reuse it.
window.__adaBootstrapPromise = null;
window.__adaEnsureBootstrap = async function ensureBootstrap() {
    const w = window;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- WHY: ??= refactor would require restructuring eslint-disable blocks around the fetch call; keeping if-form for clarity
    if (w.__adaBootstrapPromise == null) { // == matches both null and undefined (type is `Promise<unknown> | null | undefined`)
        // A FAILED bootstrap must NOT be cached as a usable promise. fetch()
        // resolves (not rejects) on a non-2xx, so without this guard a transient
        // 503/403 would be cached as a "satisfied" promise and every later
        // authFetch would await the cached-but-failed result, never re-minting the
        // ada_spa_bootstrap cookie. On a non-OK response OR a network error, null
        // the singleton so the NEXT __adaEnsureBootstrap() (e.g. the upload-401
        // re-mint+retry in upload.mjs:_reBootstrap) re-fires a fresh POST.
        // RCA: docs/design/p0-staging-web-bugs-rca-260626.md §2.5 fix #2/#3.
        const w2 = w;
        const p = (async () => {
            try {
                const resp = await fetch('/api/anon/bootstrap', { method: 'POST', credentials: 'same-origin' });
                if (!resp.ok) {
                    w2.__adaBootstrapPromise = null; // not cached as usable — allow re-mint
                }
                return resp;
            }
            catch (_e) {
                w2.__adaBootstrapPromise = null; // network error — allow re-mint; no UI surface
            }
        })();
        w.__adaBootstrapPromise = p;
    }
    return w.__adaBootstrapPromise;
};
// ─── DOMContentLoaded init ──────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
    try {
        void window.__adaEnsureBootstrap?.();
    } // fire-and-forget: bootstrap is best-effort; .catch inside ensureBootstrap
    catch (_e) { /* never let bootstrap block page init */ }
    const dismissBtn = document.getElementById('rate-limit-dismiss');
    if (dismissBtn)
        dismissBtn.addEventListener('click', () => {
            const el = document.getElementById('rate-limit-notification');
            if (el)
                el.setAttribute('hidden', '');
        });
    // Fill the upload-size-hint element with the server-injected globals.
    // The server sets window._adaMaxFileSizeMb (integer MB) and
    // window._adaMaxPagesPerFile (integer, or null = unlimited) in the
    // <head> via serve_index so the values are always server-config-derived.
    // banners.mjs / extract.mjs already read _adaMaxFileSizeMb with a 25 MB
    // fallback — setting it here makes their fallback unreachable (intended).
    (function fillUploadSizeHint() {
        const hint = document.getElementById('upload-size-hint');
        if (!hint)
            return;
        const ems = hint.querySelectorAll('em');
        if (ems.length < 2)
            return;
        const em0 = ems[0];
        const em1 = ems[1];
        if (!em0 || !em1)
            return;
        const sizeMb = (typeof window._adaMaxFileSizeMb === 'number') ? window._adaMaxFileSizeMb : 25;
        const pages = (typeof window._adaMaxPagesPerFile === 'number') ? window._adaMaxPagesPerFile : null;
        em0.textContent = `${sizeMb} MB`;
        em1.textContent = pages !== null ? String(pages) : 'unlimited';
    })();
    // Wire the user-context-alt-text toggle + textarea + counter +
    // localStorage round-trip.
    try {
        initUserContextAltText();
    }
    catch (e) {
        log.warn('user-context init failed:', e instanceof Error ? e.message : String(e));
    }
    // Wire the audience-level dropdown + localStorage round-trip.
    try {
        initAudienceLevel();
    }
    catch (e) {
        log.warn('audience-level init failed:', e instanceof Error ? e.message : String(e));
    }
    // Wire the embed-fonts-with-substitution knob (M1) + localStorage round-trip.
    try {
        initEmbedFontsWithSubstitution();
    }
    catch (e) {
        log.warn('embed-fonts-substitution init failed:', e instanceof Error ? e.message : String(e));
    }
    // Wire the structural-mod toggles + format-specific expander for
    // anonymous / not-yet-authenticated users (null = use anon localStorage
    // defaults). Re-called with the real user on SignedIn (below).
    try {
        showStructuralModToggleForUser(null);
    }
    catch (e) {
        log.warn('SM-toggle anon init failed:', e instanceof Error ? e.message : String(e));
    }
    // Render the supported-file-type BY-KIND bubbles (D1) from the shared,
    // codegen'd SSOT (ADVERTISED_BY_KIND) — the SAME module the /editor empty
    // state renders, so the grouping is defined once (no duplicated data).
    try {
        renderSupportedFormatBubblesInto('supported-format-bubbles');
    }
    catch (e) {
        log.warn('supported-format-bubbles render failed:', e instanceof Error ? e.message : String(e));
    }
    // Wire the Google Drive link-field (drive-connector Phase 4) — POSTs to the
    // landed /api/upload/drive-link route and enters the same job UI on success.
    try {
        initDriveLink();
    }
    catch (e) {
        log.warn('drive-link init failed:', e instanceof Error ? e.message : String(e));
    }
});
// ─── Upload zone factory ────────────────────────────────────────────────────
//
// Sets up click, keydown, dragover, dragleave, drop, and file-input change
// handlers on an upload zone + hidden file input. The function is local to
// init because it is only invoked once (the SPA has a single upload zone).
function wireUploadZone(zoneEl, inputEl, acceptedExts, label, section, uploadFn) {
    if (!zoneEl || !inputEl)
        return;
    // Zone-click opens the file picker, BUT we must skip the click if it
    // originated from a <label for="file-input"> inside the zone. The
    // browser's native label behavior already fires a trusted click on the
    // file input; a second `inputEl.click()` from this bubbled zone-click
    // triggers Chrome/Safari's double-activation guard and BOTH clicks get
    // cancelled — net effect: the picker never opens.
    zoneEl.addEventListener('click', (e) => {
        const t = e.target;
        if (t?.closest('label[for="' + inputEl.id + '"]'))
            return;
        inputEl.click();
    });
    zoneEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputEl.click();
        }
    });
    zoneEl.addEventListener('dragover', (e) => { e.preventDefault(); zoneEl.classList.add('dragover'); });
    zoneEl.addEventListener('dragleave', () => zoneEl.classList.remove('dragover'));
    zoneEl.addEventListener('drop', (e) => {
        void (async () => {
            e.preventDefault();
            zoneEl.classList.remove('dragover');
            const dt = (e).dataTransfer;
            if (!dt)
                return;
            // droppedFolder + hasZip declared before try so the catch block can
            // reference them for uploadToastDismiss() cleanup.
            let droppedFolder = false;
            let hasZip = false;
            try {
                // Cache webkitGetAsEntry() results SYNCHRONOUSLY before any await.
                // DataTransferItem objects become stale after an await — calling
                // webkitGetAsEntry() on a stale item throws DOMException in some
                // browsers (e.g. "not in the 'read' state"). The try now wraps these
                // calls so any throw surfaces the error banner rather than being
                // silently swallowed by the void-IIFE's implicit rejection.
                const items = dt.items ? [...dt.items] : [];
                const entries = items.map(item => item.webkitGetAsEntry ? item.webkitGetAsEntry() : null);
                droppedFolder = entries.some(entry => entry?.isDirectory);
                hasZip = [...dt.files].some(f => f.name.toLowerCase().endsWith('.zip'));
                if (droppedFolder || hasZip) {
                    uploadToastBegin(0); // total=0 triggers "Processing..." text
                }
                const folderFiles = await extractFolder(items);
                // Filter out directory entries before expandZips: browsers surface
                // dropped folders as File objects in e.dataTransfer.files
                // (type="", size=0). We already walk them via extractFolder above —
                // passing them to expandZips too would produce a spurious
                // "unsupported file type" warning for the folder itself.
                // Use the pre-cached entries (captured synchronously above) instead of
                // calling webkitGetAsEntry() again on now-stale DataTransferItem objects.
                const nonFolderFiles = [...dt.files].filter((_, i) => !entries[i]?.isDirectory);
                const plainFiles = await expandZips(nonFolderFiles, section);
                const allDropped = [...folderFiles, ...plainFiles];
                if (droppedFolder) {
                    if (folderFiles.length > 0) {
                        showSectionBanner(section, 'info', `Found ${folderFiles.length} supported file(s) in folder.`);
                    }
                    else {
                        showSectionBanner(section, 'info', `No supported files found in folder.`);
                    }
                }
                log.info('[ada-tool] dropped files:', allDropped.map(f => f.name));
                const { accepted } = filterByExtension(allDropped, acceptedExts, label, section);
                if (accepted.length > 0) {
                    uploadFn(accepted);
                }
                else if (droppedFolder || hasZip) {
                    uploadToastDismiss();
                }
            }
            catch (err) {
                // Surface drop-handler failures to the user instead of silently swallowing
                // them. The void-IIFE wrapper discards any uncaught rejection; this catch
                // ensures errors (e.g. stale DataTransferItem DOMException, JSZip load
                // failure, or an unexpected throw) show an actionable message.
                log.error('[ada-tool] drop handler error:', err instanceof Error ? err.message : String(err));
                showSectionBanner(section, 'error', 'File upload failed — please try again or use the browse button.');
                if (droppedFolder || hasZip)
                    uploadToastDismiss();
            }
        })();
    });
    inputEl.addEventListener('change', () => {
        void (async () => {
            if (inputEl.files && inputEl.files.length > 0) {
                const allFiles = await expandZips([...inputEl.files], section);
                inputEl.value = '';
                const { accepted } = filterByExtension(allFiles, acceptedExts, label, section);
                if (accepted.length > 0)
                    uploadFn(accepted);
            }
        })();
    });
}
const uploadZone = document.getElementById('upload-zone');
const fileInput = document.getElementById('file-input');
wireUploadZone(uploadZone, fileInput, REMEDIATE_EXTS, '.pptx, .docx, .pdf, .xlsx, folder, or .zip', 'remediate', async (files) => {
    // Consent gate: ALPHA + BETA stages require a one-time click-through ToS dialog.
    // Reads release_stage from the cached auth profile (populated by checkSupabaseAuth).
    // On consent decline the upload is silently dropped — the user can re-try after
    // accepting (or not), which matches "polite dismissable, not modal-trap" §3.2.
    const stage = getAuthProfile()?.release_stage ?? 'LOCAL';
    const consented = await requireConsentIfNeeded(stage);
    if (!consented) {
        log.info('[ada-tool] upload blocked — user declined ToS consent for stage:', stage);
        return;
    }
    void uploadFiles(files);
});
const adaFeatures = (window.__ada ?? { features: {} }).features;
adaFeatures.upload = true;
// The Options rail lives INSIDE .upload-zone (right column); without
// stopPropagation, clicking a toggle would bubble to the zone-click handler
// in wireUploadZone and open the file picker every time.
const optionsRail = document.querySelector('.upload-options-rail');
if (optionsRail) {
    optionsRail.addEventListener('click', (e) => e.stopPropagation());
    optionsRail.addEventListener('keydown', (e) => {
        const ke = e;
        if (ke.key === 'Enter' || ke.key === ' ')
            e.stopPropagation();
    });
}
// ─── Feedback sidebar ──────────────────────────────────────────────────────
(function initFeedback() {
    const toggle = document.getElementById('feedback-toggle');
    const form = document.getElementById('feedback-form');
    const fileInputEl = document.getElementById('feedback-file');
    const fileLabel = document.getElementById('feedback-file-label');
    const submitBtn = document.getElementById('feedback-submit');
    const msgBox = document.getElementById('feedback-message');
    const contactBox = document.getElementById('feedback-contact');
    const statusEl = document.getElementById('feedback-status');
    if (!toggle || !form || !fileInputEl || !submitBtn || !msgBox || !statusEl)
        return;
    adaFeatures.feedback = true;
    toggle.addEventListener('click', () => {
        form.style.display = form.style.display === 'none' ? 'block' : 'none';
        toggle.setAttribute('aria-expanded', form.style.display === 'none' ? 'false' : 'true');
        toggle.style.display = form.style.display === 'none' ? '' : 'none';
    });
    const closeBtn = document.getElementById('feedback-close-btn');
    if (closeBtn)
        closeBtn.addEventListener('click', () => closeFeedback());
    let droppedFile = null;
    const attachLabel = fileInputEl.closest('.feedback-attach');
    function setAttachFile(file) {
        droppedFile = file;
        if (fileLabel)
            fileLabel.textContent = file ? file.name : 'Attach file (optional)';
        if (attachLabel)
            attachLabel.classList.toggle('has-file', !!file);
    }
    fileInputEl.addEventListener('change', () => {
        setAttachFile(fileInputEl.files && fileInputEl.files.length > 0 ? fileInputEl.files.item(0) : null);
    });
    if (attachLabel) {
        attachLabel.addEventListener('dragover', (e) => { e.preventDefault(); attachLabel.classList.add('dragover'); });
        attachLabel.addEventListener('dragleave', () => attachLabel.classList.remove('dragover'));
        attachLabel.addEventListener('drop', (e) => {
            e.preventDefault();
            attachLabel.classList.remove('dragover');
            const de = e;
            if (de.dataTransfer && de.dataTransfer.files.length > 0)
                setAttachFile(de.dataTransfer.files.item(0));
        });
    }
    msgBox.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submitBtn.click();
        }
    });
    submitBtn.addEventListener('click', () => {
        void (async () => {
            const message = msgBox.value.trim();
            if (!message) {
                statusEl.textContent = 'Please enter a message.';
                return;
            }
            submitBtn.disabled = true;
            statusEl.textContent = 'Sending...';
            const file = droppedFile ?? (fileInputEl.files && fileInputEl.files.length > 0 ? fileInputEl.files[0] : null);
            const contact = contactBox ? contactBox.value.trim() : '';
            const formData = new FormData();
            formData.append('message', message);
            if (contact)
                formData.append('contact', contact);
            if (file)
                formData.append('attachment', file);
            try {
                const resp = await authFetch(`${API}/api/feedback`, { method: 'POST', body: formData });
                if (!resp.ok) {
                    const err = await resp.json().catch(() => ({}));
                    statusEl.textContent = err.detail ?? `Error ${resp.status}`;
                }
                else {
                    statusEl.style.color = 'var(--maroon)';
                    statusEl.textContent = 'Thank you! Your feedback has been received.';
                    msgBox.value = '';
                    if (contactBox)
                        contactBox.value = '';
                    fileInputEl.value = '';
                    setAttachFile(null);
                    setTimeout(() => {
                        form.style.display = 'none';
                        toggle.style.display = '';
                        statusEl.textContent = '';
                        statusEl.style.color = '';
                    }, 3000);
                }
            }
            catch (_e) {
                statusEl.textContent = 'Failed to send. Please try again.';
            }
            submitBtn.disabled = false;
        })();
    });
})();
function closeFeedback() {
    const sidebar = document.getElementById('feedback-sidebar');
    const form = document.getElementById('feedback-form');
    const toggle = document.getElementById('feedback-toggle');
    if (form)
        form.style.display = 'none';
    if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.style.display = '';
    }
    void sidebar; // Reserved for future "collapse sidebar" extension.
}
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape')
        closeFeedback();
});
document.addEventListener('click', (e) => {
    const target = e.target;
    const sidebar = document.getElementById('feedback-sidebar');
    const toggle = document.getElementById('feedback-toggle');
    const form = document.getElementById('feedback-form');
    if (sidebar && form && form.style.display !== 'none'
        && !sidebar.contains(target) && !toggle?.contains(target)) {
        closeFeedback();
    }
});
// ─── Init ───────────────────────────────────────────────────────────────────
// Check system status on load: show banner only for non-accepting or non-empty queue
// (never flicker on idle).
// eslint-disable-next-line @typescript-eslint/no-unsafe-return -- WHY: fetch().json() returns Promise<any>; full API typing is a separate effort
fetch(`${API}/api/queue`).then(async (r) => r.json()).then(q => {
    updateSystemBanner(q);
    if (q.show_donate)
        renderDonateSection();
}).catch((err) => {
    log.warn('initial queue fetch failed:', err?.message ?? err);
});
// Periodic queue refresh. pollLoop() (in polling.mjs) ALSO fetches /api/queue
// per iteration when there are active jobs, so this is a no-op cost when
// the user has pending work. When the SPA is idle, this 30s tick keeps the
// system banner fresh without burning bandwidth.
setInterval(() => {
    /* eslint-disable promise/prefer-await-to-then, @typescript-eslint/no-unsafe-return -- fire-and-forget inside setInterval callback; cannot await in sync callback; fetch JSON is untyped */
    fetch(`${API}/api/queue`).then(async (r) => r.json()).then(updateSystemBanner).catch((err) => {
        log.warn('periodic queue fetch failed:', err?.message ?? err);
    });
    /* eslint-enable promise/prefer-await-to-then, @typescript-eslint/no-unsafe-return */
}, 30000);
// Download All button.
async function downloadAll() {
    const completedIds = jobState.listJobs()
        .filter(j => j.status === window.MainJobStatus?.COMPLETED && j.has_output)
        .map(j => j.id);
    if (completedIds.length === 0)
        return;
    const btn = document.getElementById('download-all-btn');
    if (btn?.disabled)
        return;
    log.info('[ada-tool] downloading all:', completedIds.length, 'file(s)');
    if (btn) {
        btn.textContent = 'Zipping...';
        btn.disabled = true;
        btn.style.opacity = '0.6';
    }
    try {
        const resp = await authFetch(`${API}/api/jobs/download-all`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ job_ids: completedIds }),
        });
        if (!resp.ok)
            throw new Error(`Server returned ${resp.status}`);
        const triggerBlobDownload = async (blob, totalBytes) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'docable-results.zip';
            a.style.display = 'none';
            document.body.appendChild(a);
            await sleep(100);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 1000);
            log.info('[ada-tool] download-all: zip downloaded (' + (totalBytes / 1024).toFixed(0) + ' KB)');
        };
        if (!resp.body || typeof resp.body.getReader !== 'function') {
            log.info('[ada-tool] download-all: streaming API unavailable, using blob fallback');
            if (btn) {
                btn.textContent = 'Downloading…';
            }
            const blob = await resp.blob();
            await triggerBlobDownload(blob, blob.size);
            return;
        }
        const STREAM_STALL_TIMEOUT_MS = 60000;
        const reader = resp.body.getReader();
        const chunks = [];
        let totalBytes = 0;
        let lastProgressUpdate = 0;
        while (true) {
            let stallTimer;
            const stallPromise = new Promise((_, reject) => {
                stallTimer = setTimeout(() => reject(new Error('Stream stalled (no data for 60 s)')), STREAM_STALL_TIMEOUT_MS);
            });
            let readResult;
            try {
                readResult = await Promise.race([reader.read(), stallPromise]);
            }
            catch (stallErr) {
                void reader.cancel(); // fire-and-forget: cancelling a reader that already errored
                throw stallErr;
            }
            finally {
                if (stallTimer)
                    clearTimeout(stallTimer);
            }
            const { done, value } = readResult;
            if (done)
                break;
            if (value) {
                chunks.push(value);
                totalBytes += value.byteLength;
            }
            const now = Date.now();
            if (btn && now - lastProgressUpdate >= 250) {
                lastProgressUpdate = now;
                const mb = (totalBytes / (1024 * 1024)).toFixed(1);
                btn.textContent = `Downloading ${mb} MB…`;
            }
        }
        const blob = new Blob(chunks, { type: 'application/zip' });
        await triggerBlobDownload(blob, totalBytes);
    }
    catch (e) {
        log.error('download-all failed:', e instanceof Error ? e.message : String(e));
        showSectionBanner('remediate', 'error', `Download interrupted — please retry. (${e instanceof Error ? e.message : String(e)})`);
    }
    finally {
        if (btn) {
            btn.textContent = 'Download All (.zip)';
            btn.disabled = false;
            btn.style.opacity = '';
        }
    }
}
const _dlAllBtn = document.getElementById('download-all-btn');
if (_dlAllBtn)
    _dlAllBtn.addEventListener('click', () => { void downloadAll(); });
// Clear History button.
function clearHistory() {
    const completedStatus = window.MainJobStatus?.COMPLETED;
    const failedStatus = window.MainJobStatus?.FAILED;
    const toRemove = jobState.listJobs()
        .filter(j => j.status === completedStatus || j.status === failedStatus)
        .map(j => j.id);
    for (const id of toRemove) {
        jobState.removeJob(id);
        jobState.addClearedId(id);
        jobState.clearMonotonicCache(id);
    }
    jobState.saveClearedIds();
    jobState.saveJobIds();
    render();
    const uz = document.getElementById('upload-zone');
    if (uz)
        uz.focus();
    log.info('[ada-tool] cleared', toRemove.length, 'completed/failed job(s)');
}
const _clearBtn = document.getElementById('clear-history-btn');
if (_clearBtn)
    _clearBtn.addEventListener('click', clearHistory);
// Cancel All button.
const _cancelAllBtn = document.getElementById('cancel-all-btn');
if (_cancelAllBtn)
    _cancelAllBtn.addEventListener('click', () => {
        void (async () => {
            _cancelAllBtn.disabled = true;
            _cancelAllBtn.textContent = 'Cancelling...';
            try {
                const resp = await authFetch(`${API}/api/cancel-all`, { method: 'POST' });
                if (resp.ok) {
                    const data = await resp.json();
                    log.info('[ada-tool] cancel-all:', data);
                    for (const id of jobState.listJobIds()) {
                        const job = jobState.getJob(id);
                        if (job && ACTIVE_STATUSES.has(job.status)) {
                            jobState.removeJob(id);
                            jobState.clearMonotonicCache(id);
                        }
                    }
                    for (const [pendingId, payload] of jobState.listPendingRetries()) {
                        if (payload.retryTimer)
                            clearInterval(payload.retryTimer);
                        jobState.removePendingRetry(pendingId);
                    }
                    jobState.saveJobIds();
                    render();
                }
            }
            catch (e) {
                log.error('cancel-all error:', e instanceof Error ? e.message : String(e));
            }
            _cancelAllBtn.disabled = false;
            _cancelAllBtn.textContent = 'Cancel All';
        })();
    });
// Top-bar padding: #top-bar-stack is position:fixed;top:0, so it overlays the
// header (h1 "DocAble") unless the body is padded down by the stack's height.
// The stack grows AFTER initial load — site-header.ts appends the nav and
// env-banner.ts appends the env/stage banner post-/api/auth/me — and it also
// reflows on mobile when the user-bar wraps. A one-shot padding set on load
// would leave the banner overlapping the logo (prod symptom, 260723). Observe
// the stack's size instead of the text so any async height change (banner, nav,
// user-bar wrap) reflows the padding; keeps resize/orientationchange as a
// fallback for browsers without ResizeObserver.
function updateTopBarPadding() {
    const stack = document.getElementById('top-bar-stack');
    if (stack) {
        document.body.style.paddingTop = stack.offsetHeight + 'px';
    }
}
window.addEventListener('resize', updateTopBarPadding);
window.addEventListener('orientationchange', updateTopBarPadding);
(function observeTopBarHeight() {
    const stack = document.getElementById('top-bar-stack');
    if (!stack)
        return;
    updateTopBarPadding(); // initial set — the static body padding-top does not account for the fixed stack
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(updateTopBarPadding).observe(stack);
    }
})();
// Feedback close button.
const _feedbackCloseBtn = document.getElementById('feedback-close-btn');
if (_feedbackCloseBtn)
    _feedbackCloseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closeFeedback();
    });
// Capture in-progress "Report a problem" drafts so SSE/polling re-renders of
// the done-list don't blow them away.
document.addEventListener('input', (e) => {
    const target = e.target;
    const ta = target?.closest('.job-feedback-textarea');
    if (!ta)
        return;
    const card = ta.closest('.job-card');
    const link = card?.querySelector('[data-feedback-job]');
    const jobId = link?.getAttribute('data-feedback-job');
    if (!jobId)
        return;
    if (!jobState.hasReportDialog(jobId))
        jobState.setReportDialog(jobId, { text: '' });
    const dlg = jobState.getReportDialog(jobId);
    if (dlg)
        dlg.text = ta.value;
});
// ─── Mega delegated click listener (route-by-data-attribute) ────────────────
// Per docs/design/app-js-split-plan-260522.md §6.4: extracted verbatim from
// app.js. The listener is the single dispatcher for ~16 routes; routes that
// previously called bare function names now call the imported equivalents.
document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target)
        return;
    const reportLink = target.closest('[data-download-report]');
    if (reportLink) {
        e.preventDefault();
        const jid = reportLink.getAttribute('data-download-report');
        if (jid)
            void downloadChangeReport(jid); // fire-and-forget: link-click download; errors logged inside
        return;
    }
    const logoutLink = target.closest('[data-action="logout"]');
    if (logoutLink) {
        e.preventDefault();
        void logout(); // fire-and-forget: logout includes redirect; errors don't need surfacing
        return;
    }
    const cancelJobEl = target.closest('[data-cancel-job]');
    if (cancelJobEl) {
        const jobId = cancelJobEl.getAttribute('data-cancel-job');
        if (!jobId)
            return;
        e.preventDefault();
        cancelJobEl.disabled = true;
        cancelJobEl.textContent = '...';
        const tracked = jobState.getJob(jobId);
        if (tracked && window.setMainJobStatus) {
            window.setMainJobStatus(tracked, window.MainJobStatus?.CANCELLING ?? 'cancelling');
        }
        render();
        void (async () => {
            try {
                const resp = await authFetch(`${API}/api/jobs/${encodeURIComponent(jobId)}/cancel`, { method: 'POST' });
                if (resp.ok) {
                    jobState.removeJob(jobId);
                    jobState.clearMonotonicCache(jobId);
                    jobState.saveJobIds();
                    render();
                    log.info('[ada-tool] cancelled job', jobId);
                }
                else {
                    const data = await resp.json().catch(() => ({}));
                    log.warn('cancel failed:', data.detail ?? `HTTP ${resp.status}`);
                    cancelJobEl.disabled = false;
                    cancelJobEl.textContent = '×';
                }
            }
            catch (err) {
                log.error('cancel error:', err instanceof Error ? err.message : String(err));
                cancelJobEl.disabled = false;
                cancelJobEl.textContent = '×';
            }
        })();
        return;
    }
    const dismissJobEl = target.closest('[data-dismiss-job]');
    if (dismissJobEl) {
        const jobId = dismissJobEl.getAttribute('data-dismiss-job');
        if (jobId && jobState.hasJob(jobId)) {
            jobState.removeJob(jobId);
            jobState.clearMonotonicCache(jobId);
            jobState.saveJobIds();
            render();
            const nextCard = document.querySelector('#done-list .dismiss-btn');
            if (nextCard) {
                nextCard.focus();
            }
            else {
                const col = document.querySelector('#done-list');
                const colTitle = col?.closest('.job-column')?.querySelector('.column-title');
                colTitle?.focus();
            }
            log.info('[ada-tool] dismissed remediation job', jobId);
        }
        return;
    }
    const retryJobEl = target.closest('[data-retry-job]');
    if (retryJobEl) {
        const oldJobId = retryJobEl.getAttribute('data-retry-job');
        if (!oldJobId)
            return;
        e.preventDefault();
        retryJobEl.disabled = true;
        retryJobEl.textContent = 'Retrying...';
        void (async () => {
            try {
                const resp = await authFetch(`${API}/api/jobs/${encodeURIComponent(oldJobId)}/retry`, { method: 'POST' });
                if (!resp.ok) {
                    const data = await resp.json().catch(() => ({}));
                    throw new Error(data.detail ?? `HTTP ${resp.status}`);
                }
                const newJob = await resp.json();
                log.info('[ada-tool] retry created new job', newJob.id, 'from', oldJobId);
                jobState.removeJob(oldJobId);
                jobState.trackJob(newJob);
                jobState.saveJobIds();
                render();
                startPolling();
            }
            catch (ex) {
                log.error('retry failed:', ex instanceof Error ? ex.message : String(ex));
                retryJobEl.disabled = false;
                retryJobEl.textContent = 'Retry';
                alert('Retry failed: ' + (ex instanceof Error ? ex.message : String(ex)));
            }
        })();
        return;
    }
    const remediateAgainEl = target.closest('[data-remediate-again]');
    if (remediateAgainEl) {
        const oldJobId = remediateAgainEl.getAttribute('data-remediate-again');
        if (!oldJobId)
            return;
        e.preventDefault();
        remediateAgainEl.disabled = true;
        remediateAgainEl.textContent = '⌛';
        void (async () => {
            try {
                const resp = await authFetch(`${API}/api/jobs/${encodeURIComponent(oldJobId)}/retry`, { method: 'POST' });
                if (!resp.ok) {
                    const data = await resp.json().catch(() => ({}));
                    throw new Error(data.detail ?? `HTTP ${resp.status}`);
                }
                const newJob = await resp.json();
                log.info('[ada-tool] re-remediate created new job', newJob.id, 'from', oldJobId);
                jobState.trackJob(newJob);
                jobState.saveJobIds();
                render();
                startPolling();
            }
            catch (err) {
                log.error('re-remediate failed:', err instanceof Error ? err.message : String(err));
                remediateAgainEl.disabled = false;
                remediateAgainEl.textContent = '↻';
                alert('Re-remediate failed: ' + (err instanceof Error ? err.message : String(err)));
            }
        })();
        return;
    }
    const toggleDetails = target.closest('[data-action="toggle-details"]');
    if (toggleDetails) {
        const msg = toggleDetails.closest('.upload-message');
        const details = msg?.querySelector('.msg-details');
        if (details) {
            details.classList.toggle('hidden');
            toggleDetails.innerHTML = details.classList.contains('hidden') ? '&#9656; Details' : '&#9662; Details';
        }
        return;
    }
    const dismissMsg = target.closest('[data-action="dismiss-message"]');
    if (dismissMsg) {
        const msg = dismissMsg.closest('.upload-message');
        if (msg)
            msg.remove();
        updateMessagesSummary();
        return;
    }
    if (target.closest('[data-action="clear-upload-messages"]')) {
        clearUploadMessages();
        return;
    }
    const navEl = target.closest('[data-action="navigate"]');
    if (navEl) {
        if (navEl.dataset.href)
            window.location.href = navEl.dataset.href;
        return;
    }
    if (target.closest('[data-action="link-institution"]')) {
        void linkInstitution(); // fire-and-forget: institution-link link click
        return;
    }
    // Auth-error retry (RetryHandler that lived at app.js line ~4733).
    if (target.closest('[data-action="auth-retry"]')) {
        e.preventDefault();
        // Trigger a fresh auth-profile fetch through checkSupabaseAuth's path.
        void checkSupabaseAuth(); // fire-and-forget: auth retry from user click; errors handled inside
        return;
    }
    // Inline "Report a problem" toggle (completed job cards).
    const feedbackLink = target.closest('[data-feedback-job]');
    if (feedbackLink && !feedbackLink.classList.contains('job-feedback-sent')) {
        e.preventDefault();
        const jobId = feedbackLink.getAttribute('data-feedback-job');
        if (!jobId)
            return;
        const card = feedbackLink.closest('.job-card');
        const formEl = card?.querySelector('.job-feedback-form');
        if (formEl) {
            const isOpen = formEl.style.display !== 'none';
            formEl.style.display = isOpen ? 'none' : 'block';
            feedbackLink.textContent = isOpen ? '▸ Report a problem' : '▾ Report a problem';
            if (isOpen) {
                jobState.removeReportDialog(jobId);
            }
            else {
                jobState.setReportDialog(jobId, { text: '' });
                const ta = formEl.querySelector('.job-feedback-textarea');
                if (ta)
                    ta.focus();
            }
        }
        return;
    }
    const feedbackSubmit = target.closest('[data-feedback-submit]');
    if (feedbackSubmit) {
        e.preventDefault();
        const jobId = feedbackSubmit.getAttribute('data-feedback-submit');
        if (!jobId)
            return;
        const card = feedbackSubmit.closest('.job-card');
        const formEl = card?.querySelector('.job-feedback-form');
        const linkEl = card?.querySelector('[data-feedback-job]');
        const ta = formEl?.querySelector('.job-feedback-textarea');
        const message = ta ? ta.value.trim() : '';
        if (!message) {
            if (ta)
                ta.focus();
            return;
        }
        feedbackSubmit.disabled = true;
        feedbackSubmit.textContent = 'Sending…';
        void (async () => {
            try {
                const fd = new FormData();
                fd.append('message', message);
                fd.append('job_id', jobId);
                const resp = await authFetch(`${API}/api/feedback`, { method: 'POST', body: fd });
                if (resp.ok) {
                    if (formEl)
                        formEl.style.display = 'none';
                    if (linkEl) {
                        linkEl.textContent = 'Sent — thank you';
                        linkEl.classList.add('job-feedback-sent');
                    }
                    jobState.removeReportDialog(jobId);
                    log.info('[ada-tool] inline feedback submitted for job', jobId);
                }
                else {
                    feedbackSubmit.disabled = false;
                    feedbackSubmit.textContent = 'Send';
                    const err = await resp.json().catch(() => ({}));
                    log.warn('inline feedback failed:', err.detail ?? `HTTP ${resp.status}`);
                    if (ta)
                        ta.setAttribute('placeholder', 'Something went wrong, please try again.');
                }
            }
            catch (err) {
                feedbackSubmit.disabled = false;
                feedbackSubmit.textContent = 'Send';
                log.error('inline feedback error:', err instanceof Error ? err.message : String(err));
            }
        })();
        return;
    }
});
// ─── Changelog hover tooltip ───────────────────────────────────────────────
// Floating tooltip when hovering over "List of changes" links. Uses cached
// report data from jobState — no extra fetch needed.
(function initChangelogTooltip() {
    let tooltipEl = null;
    let showTimer = null;
    let hideTimer = null;
    let currentJobId = null;
    function buildTooltipHTML(job) {
        if (!job)
            return null;
        const r = job.report;
        if (!r)
            return null;
        const env = readEnvelope(job);
        const cats = env.categories || [];
        let n;
        if (env.kind === 'envelope') {
            n = env.totalChanges;
        }
        else {
            n = env.totalChanges
                || cats.reduce((sum, g) => sum + (g.changes ? g.changes.length : 0), 0)
                || 0;
        }
        if (n === 0)
            return null;
        const filename = job.filename ?? '';
        const fileExt = fileExtFromFilename(filename);
        const unitLabel = unitLabelForExt(fileExt);
        let html = `<div class="tt-summary">${esc(String(n))} ${n === 1 ? 'change' : 'changes'} applied</div>`;
        for (const g of cats) {
            const items = g.changes ?? [];
            if (items.length === 0)
                continue;
            const label = (g.label ?? g.category) ?? 'Other';
            html += `<div class="tt-category">${esc(label)} (${items.length})</div>`;
            for (const item of items) {
                const loc = item.page_or_slide ?? item.slide;
                const locStr = loc ? `<span class="tt-loc">${esc(unitLabel)} ${esc(String(loc))}: </span>` : '';
                const desc = (item.description ?? item.change) ?? '';
                html += `<div class="tt-item">${locStr}${esc(desc)}</div>`;
            }
        }
        return html;
    }
    function showTooltip(linkEl, jobId) {
        const job = jobState.getJob(jobId);
        if (!job || !job.report)
            return;
        const html = buildTooltipHTML(job);
        if (!html)
            return;
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.className = 'changelog-tooltip';
            tooltipEl.addEventListener('mouseenter', () => { cancelHide(); });
            tooltipEl.addEventListener('mouseleave', () => { scheduleHide(); });
            document.body.appendChild(tooltipEl);
        }
        tooltipEl.innerHTML = html;
        tooltipEl.style.display = 'block';
        currentJobId = jobId;
        const rect = linkEl.getBoundingClientRect();
        const ttHeight = tooltipEl.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        let top;
        if (spaceBelow < ttHeight + 8 && rect.top > ttHeight + 8) {
            top = rect.top - ttHeight - 4;
        }
        else {
            top = rect.bottom + 4;
        }
        let left = Math.min(rect.left, window.innerWidth - 380);
        left = Math.max(8, left);
        tooltipEl.style.top = top + 'px';
        tooltipEl.style.left = left + 'px';
    }
    function hideTooltip() {
        if (tooltipEl)
            tooltipEl.style.display = 'none';
        currentJobId = null;
    }
    function cancelHide() {
        if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
    }
    function scheduleHide() {
        cancelHide();
        hideTimer = setTimeout(hideTooltip, 250);
    }
    function cancelShow() {
        if (showTimer) {
            clearTimeout(showTimer);
            showTimer = null;
        }
    }
    document.addEventListener('mouseover', (e) => {
        const t = e.target;
        const link = t?.closest('.report-link[data-download-report]');
        if (!link)
            return;
        const jobId = link.getAttribute('data-download-report');
        if (!jobId)
            return;
        if (currentJobId === jobId) {
            cancelHide();
            return;
        }
        cancelShow();
        showTimer = setTimeout(() => showTooltip(link, jobId), 100);
    });
    document.addEventListener('mouseout', (e) => {
        const t = e.target;
        const link = t?.closest('.report-link[data-download-report]');
        if (!link)
            return;
        cancelShow();
        scheduleHide();
    });
})();
// ─── Build-info footer ──────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-unsafe-return -- WHY: fetch().json() returns Promise<any>; full API typing is a separate effort
fetch(`${API}/api/version`).then(async (r) => r.json()).then(data => {
    const el = document.getElementById('app-last-updated');
    if (el && data.build_time) {
        try {
            const d = new Date(data.build_time);
            const fmt = d.toLocaleString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit', hour12: true,
            });
            el.textContent = `Built ${fmt}`;
            adaFeatures.footer_build_time = true;
        }
        catch {
            el.textContent = '';
        }
    }
    else if (el) {
        el.textContent = `Last updated: v${data.version}`;
    }
    // Dev-login buttons (TEST_MODE only). Reveal them from the authoritative
    // `test_mode` flag on /api/version — the SAME signal the server uses to
    // register the /api/auth/dev-login route, so the two can never disagree.
    // Previously this was a `fetch('/api/auth/dev-login', {method:'OPTIONS'})`
    // feature-probe; on staging/prod the route is (correctly) unregistered, so
    // that probe 404'd and the browser logged a console error that tripped the
    // VC-static console-noise gate. Gating on test_mode fires NO request on
    // non-dev envs. See docs/design/staging-gating-failures-rca-260629.md §2.
    if (data.test_mode === true) {
        const btn1 = document.getElementById('dev-login-btn');
        const btn2 = document.getElementById('dev-login-free-btn');
        if (btn1)
            btn1.style.display = '';
        if (btn2)
            btn2.style.display = '';
    }
}).catch(() => { });
// ─── Restore tracked job IDs from localStorage ──────────────────────────────
void (async function restoreJobs() {
    let savedIds = [];
    try {
        savedIds = JSON.parse(localStorage.getItem('ada_job_ids') ?? '[]');
    }
    catch (_e) { /* Safari private mode */ }
    if (savedIds.length === 0)
        return;
    if (savedIds.length > 50) {
        savedIds = savedIds.slice(-50);
        try {
            localStorage.setItem('ada_job_ids', JSON.stringify(savedIds));
        }
        catch (_e) { /* ignore */ }
    }
    log.info('[ada-tool] restoring', savedIds.length, 'tracked job(s) from localStorage');
    const results = await Promise.allSettled(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- WHY: fetch job JSON is any-typed; callback returns any | null; full API typing is a separate effort
    savedIds.map(async (id) => fetch(`${API}/api/jobs/${id}`).then(async (resp) => {
        if (!resp.ok)
            return null;
        const job = await resp.json();
        if (job.status === window.MainJobStatus?.CANCELLED)
            return null;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- WHY: job is any-typed from fetch JSON; full typing is a separate effort
        return job;
    })));
    for (const result of results) {
        if (result.status === 'fulfilled' && result.value) {
            jobState.trackJob(result.value);
        }
    }
    jobState.saveJobIds();
    render();
    const hasActive = jobState.hasAnyJobMatching(j => ACTIVE_STATUSES.has(j.status));
    if (hasActive)
        startPolling();
})();
// ─── loadUserJobHistory — IoC callback registered with auth ────────────────
async function loadUserJobHistory() {
    try {
        const resp = await authFetch(`${API}/api/account/jobs?per_page=100&page=1`);
        if (!resp.ok)
            return;
        const data = await resp.json();
        if (!Array.isArray(data.jobs))
            return;
        let changed = false;
        for (const job of data.jobs) {
            if (!job.id)
                continue;
            if (job.status === window.MainJobStatus?.CANCELLED)
                continue;
            if (jobState.isCleared(job.id))
                continue;
            if (!jobState.hasJob(job.id) && jobState.trackJob(job))
                changed = true;
        }
        if (changed) {
            jobState.saveJobIds();
            render();
        }
        log.info('[ada-tool] loaded', data.jobs.length, 'jobs from account history (total:', data.total, ')');
    }
    catch (e) {
        log.warn('loadUserJobHistory failed:', e);
    }
}
// Register the post-auth callback. auth-and-billing's AuthClient fires this
// once per transition to SignedIn.
registerOnAuthenticated(() => { void loadUserJobHistory(); }); // fire-and-forget: post-auth history load
// Re-wire SM toggles + format-specific expander from the authenticated user's
// stored prefs. showStructuralModToggleForUser is idempotent (guarded by
// _adaSmBound) — re-calling with the real user after the anon init above
// rebinds handlers cleanly and populates _smStoredDefault from server prefs.
registerOnAuthenticated((profile) => {
    try {
        showStructuralModToggleForUser(profile.user ?? null);
    }
    catch (e) {
        log.warn('SM-toggle auth init failed:', e instanceof Error ? e.message : String(e));
    }
});
// ─── Server-Sent Events ────────────────────────────────────────────────────
const _SSE_READYSTATE_CLOSED = 2;
let evtSource = null;
function _attachSseHandlers(es) {
    es.onmessage = (event) => {
        try {
            const job = JSON.parse(event.data);
            // Delegate the per-frame branching to sse-handler.ts. The extraction
            // exists so the parity-with-polling invariant is seam-testable (see
            // web/static/spa/test/sse-handler.seam.test.mjs). Both handlers MUST
            // mirror each other on monotonic-cache clear at terminal status —
            // skipping either was the code-hygiene-sweep HIGH #2 bug.
            handleSseFrame(job, {
                render: () => render(),
                refreshSystemBanner: () => refreshSystemBanner(),
                log,
            });
        }
        catch (e) {
            log.warn('SSE parse error:', e instanceof Error ? e.message : String(e));
        }
    };
    es.onerror = () => { log.info('[ada-tool] SSE reconnecting...'); };
}
function _startSse() {
    const es = new EventSource('/api/events');
    _attachSseHandlers(es);
    return es;
}
evtSource = _startSse();
adaFeatures.sse = true;
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible'
        && evtSource?.readyState === _SSE_READYSTATE_CLOSED) {
        log.info('[ada-tool] SSE: tab visible + connection closed, reconnecting');
        try {
            evtSource.close();
        }
        catch (_e) { /* best-effort: already closed */ }
        evtSource = _startSse();
    }
});
// ─── Supabase auth boot ────────────────────────────────────────────────────
// REAL BUG fix (260523 Phase JL.B no-floating-promises sweep): the prior
// `checkSupabaseAuth().then(() => {...})` silently swallowed rejections.
// If supabase init failed (network / misconfig), the `data-ada-init` flag
// never got set, leaving any caller polling for it stuck. .catch() now
// logs and still sets the flag so the boot sequence continues.
checkSupabaseAuth()
    .catch((err) => {
    log.warn('[init] checkSupabaseAuth failed; continuing without auth session:', err);
})
    .finally(() => {
    if (window.__ada)
        window.__ada.initialized = true;
    document.body.setAttribute('data-ada-init', 'true');
});
// ─── OAuth + email auth buttons ─────────────────────────────────────────────
document.getElementById('google-login-btn')?.addEventListener('click', () => { void signInWithGoogle(); });
document.getElementById('github-login-btn')?.addEventListener('click', () => { void signInWithGithub(); });
document.getElementById('microsoft-login-btn')?.addEventListener('click', () => { void signInWithMicrosoft(); });
document.getElementById('oauth-close-btn')?.addEventListener('click', () => {
    const panel = document.getElementById('oauth-login');
    if (panel)
        panel.classList.add('hidden');
});
document.getElementById('email-login-btn')?.addEventListener('click', () => {
    const form = document.getElementById('email-auth-form');
    if (form)
        form.style.display = form.style.display === 'none' ? '' : 'none';
});
document.getElementById('email-signin-btn')?.addEventListener('click', () => {
    void (async () => {
        const sb = window.adatool?.supabase;
        const sbClient = sb && typeof sb.getClient === 'function' ? sb.getClient() : null;
        if (!sbClient?.auth?.signInWithPassword)
            return;
        const email = document.getElementById('email-auth-email')?.value?.trim();
        const password = document.getElementById('email-auth-password')?.value;
        const status = document.getElementById('email-auth-status');
        if (!email || !password) {
            if (status)
                status.textContent = 'Enter email and password.';
            return;
        }
        if (status)
            status.textContent = 'Signing in...';
        const { error } = await sbClient.auth.signInWithPassword({ email, password });
        if (error) {
            if (status)
                status.textContent = error.message ?? 'Sign-in failed';
        }
        else {
            window.location.reload();
        }
    })();
});
document.getElementById('email-signup-btn')?.addEventListener('click', () => {
    void (async () => {
        const sb = window.adatool?.supabase;
        const sbClient = sb && typeof sb.getClient === 'function' ? sb.getClient() : null;
        if (!sbClient?.auth?.signUp)
            return;
        const email = document.getElementById('email-auth-email')?.value?.trim();
        const password = document.getElementById('email-auth-password')?.value;
        const status = document.getElementById('email-auth-status');
        if (!email || !password) {
            if (status)
                status.textContent = 'Enter email and password.';
            return;
        }
        if (password.length < 6) {
            if (status)
                status.textContent = 'Password must be at least 6 characters.';
            return;
        }
        if (status)
            status.textContent = 'Creating account...';
        const { error } = await sbClient.auth.signUp({ email, password });
        if (error) {
            if (status)
                status.textContent = error.message ?? 'Sign-up failed';
        }
        else {
            if (status)
                status.textContent = 'Check your email to verify your account.';
        }
    })();
});
// ─── Dev login buttons (TEST_MODE only) ────────────────────────────────────
// Button VISIBILITY is gated on /api/version's `test_mode` flag (wired into the
// build-info version fetch above) — NOT a 404-generating OPTIONS probe, which
// polluted the home-page console on staging/prod. Here we only wire the click
// handlers; they are no-ops while the buttons stay display:none.
// See docs/design/staging-gating-failures-rca-260629.md §2.
setupDevBtn('dev-login-btn', 'purdue');
setupDevBtn('dev-login-free-btn', 'free');
// ─── Manage-account / manage-history handoff-token nav ─────────────────────
document.addEventListener('click', (e) => {
    void (async () => {
        const target = e.target;
        const link = target?.closest('.manage-account-link') ?? target?.closest('.manage-history-link');
        if (!link)
            return;
        e.preventDefault();
        const targetPath = link.classList.contains('manage-history-link') ? '/history' : '/account';
        const sb = window.adatool?.supabase;
        const sbClient = sb && typeof sb.getClient === 'function' ? sb.getClient() : null;
        let token = null;
        if (sbClient?.auth?.getSession) {
            const { data: { session } } = await sbClient.auth.getSession();
            if (session?.access_token)
                token = session.access_token;
        }
        if (!token) {
            try {
                token = localStorage.getItem('_dev_access_token');
            }
            catch (ex) {
                log.warn('localStorage unavailable:', ex);
                token = null;
            }
        }
        if (token && sb?.setHandoffToken) {
            sb.setHandoffToken(token);
        }
        window.location.href = targetPath;
    })();
});
// ─── Test hooks — expose internal state/functions so e2e tests can stage
// synthetic jobs without actually uploading files (e.g. failed-job cards
// with long errors for the render-truncation test). These are intentionally
// minimal and not used by production code.
window.__adatoolTest = {
    setJob: (job) => {
        jobState.trackJob(job);
    },
    clearJobs: () => { jobState.clearAllJobs(); },
    render: () => render(),
};
// Suppress unused-import warnings for re-exports kept for type checking only.
void _pollLoop;
void _showRateLimitNotification;
